import type {
  Category,
  DownloadJobType,
  DownloadRoute,
  DownloadStage,
  DownloadStatus,
  GallerySort,
  GalleryState,
  RequestedRoute,
  Role,
  SyncStreamName,
  Verdict,
} from "@volgo/core/contract";
import type { CSSProperties } from "react";

/** Screen names of the contract's values. One table per value set, so a thing has one name everywhere. */

export const CATEGORY_INFO: Record<Category, { label: string; color: string }> = {
  Doujinshi: { label: "동인지", color: "var(--doujin)" },
  Manga: { label: "만화", color: "var(--manga)" },
  "Artist CG": { label: "작가 CG", color: "var(--acg)" },
  "Game CG": { label: "게임 CG", color: "var(--gcg)" },
  Western: { label: "서양", color: "var(--west)" },
  "Non-H": { label: "비성인", color: "var(--nonh)" },
  "Image Set": { label: "이미지 모음", color: "var(--iset)" },
  Cosplay: { label: "코스프레", color: "var(--cos)" },
  "Asian Porn": { label: "아시안", color: "var(--asian)" },
  Misc: { label: "기타", color: "var(--misc)" },
  Private: { label: "비공개", color: "var(--misc)" },
};

/** Sets `--c`, the category colour the card line and the category mark read. */
export function categoryStyle(category: Category): CSSProperties {
  return { "--c": CATEGORY_INFO[category].color } as CSSProperties;
}

export const NAMESPACE_LABELS: Record<string, string> = {
  artist: "작가",
  group: "그룹",
  parody: "원작",
  character: "캐릭터",
  female: "여성",
  male: "남성",
  mixed: "혼성",
  language: "언어",
  cosplayer: "코스어",
  location: "장소",
  other: "기타",
  reclass: "재분류",
  temp: "임시",
};

export const STATE_LABELS: Record<GalleryState, string> = {
  listed: "등록된 것",
  expunged: "일반 목록 제외",
  replaced: "새 판으로 바뀐 것",
  removed: "원본 삭제",
};

export const SORT_LABELS: Record<GallerySort, string> = {
  gid_desc: "최신 등록순",
  gid_asc: "오래된 등록순",
  posted_desc: "최근 게시순",
  posted_asc: "오래된 게시순",
};

export const REQUESTED_ROUTE_LABELS: Record<RequestedRoute, string> = {
  auto: "자동",
  hitomi: "Hitomi",
  archive: "Archive",
};

export const ROUTE_LABELS: Record<DownloadRoute, string> = { hitomi: "Hitomi", archive: "Archive" };

export const DOWNLOAD_STATUS_LABELS: Record<DownloadStatus, string> = {
  queued: "기다리는 중",
  running: "받는 중",
  done: "다 받음",
  failed: "실패",
};

/** where a download came from (discord Spec 6.1) */
export const ORIGIN_LABELS: Record<DownloadJobType["origin"], string> = {
  web: "웹",
  bot_read: "봇 읽기",
  bot_search: "봇 검색",
  rule: "규칙",
};

/** queue priority 1..3 (discord Spec A-5): lower goes first */
export const PRIORITY_LABELS: Record<number, string> = { 1: "1 읽기·웹", 2: "2 봇 검색", 3: "3 규칙" };

export const STAGE_LABELS: Record<DownloadStage, string> = {
  starting: "준비",
  form: "Archive 확인",
  purchase: "Archive 구매",
  fetch: "다운로드",
  extract: "압축 해제",
  upload: "업로드",
  finalize: "마무리",
};

export const ROLE_LABELS: Record<Role, string> = { admin: "관리자", user: "사용자", guest: "접근 키 손님" };

export const STREAM_LABELS: Record<SyncStreamName, string> = {
  "ex-head": "새 갤러리 목록",
  "ex-expunged": "expunged 상태 확인",
  meta: "정보 채우기·갱신",
};

export const VERDICT_LABELS: Record<Verdict, string> = {
  ok: "정상",
  blank: "빈 응답",
  login_required: "로그인 필요",
  banned: "접속 차단",
  unknown: "알 수 없는 응답",
};
